import DownloaderUploaderCst from "@components/DownloaderUploaderCst";
import {
  MultiSelectCst,
  RadioCst,
  SingleCheckboxCst,
} from "@components/InputCst";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Step4Props,
  ValuesInterface,
} from "@pages/Contents/Corporate/CollectCampaigns/types";
import { downloadFile, ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCollectCampaignState } from "../../context/CollectCampaignContext";

const Step4: React.FC<Step4Props> = ({ errors }) => {
  const theme = useTheme();
  const { t } = useTranslation("notification-page");
  const { state, dispatch } = useCollectCampaignState();
  const { id, idCampaign } = useParams();
  const [allUsersSelected, setAllUsersSelected] = useState<boolean>(
    !(state.users?.groups?.length ?? false),
  );
  const [hasExcludedUsers, setHasExcludedUsers] = useState<boolean>(
    !!state.users?.exclusions,
  );

  const { dataTable: users } = useFetchTable(
    `${ServicesURL.groups.replace(":idCorporate", id ? id : "1")}/groups?page_size=200`,
    PagesID["corporate.groups-dynamic"],
  );

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const getOptionsUsers: { label: string; value: number }[] = useMemo(() => {
    if (!users?.output) return [];
    return users.output.map((user: ValuesInterface) => ({
      label: user.name,
      value: user.id,
    }));
  }, [users]);

  const options = [
    {
      label: t("all_users"),
      value: true,
    },
    {
      label: t("specific_users"),
      value: false,
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        padding: "16px",
        borderRadius: "3px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          paddingX: 0,
          marginBottom: "16px",
          gap: 1,
        }}
      >
        <RadioCst
          key={"lo"}
          id={"lo"}
          value={allUsersSelected}
          sx={{
            width: "100%",
            "& .MuiFormControlLabel-root": {
              margin: "0 10px",
              border: `1px solid ${theme.palette.divider}`,
              flex: 1,
              whiteSpace: "nowrap",
              paddingX: "20px",
              borderRadius: "3px",
              textAlign: "start",
            },
          }}
          onChange={(e) => {
            const value = String(e).toLowerCase() === "true";
            if (value) {
              dispatch({
                type: "SET_USERS",
                payload: { ...state.users, groups: undefined },
              });
            }
            setAllUsersSelected(value);
          }}
          options={options}
        />
      </Box>
      <Box
        sx={{
          margin: "0 10px",
          marginBottom: "16px",
          gap: 1,
        }}
      >
        <MultiSelectCst
          id={"usersGroups"}
          label={`${t("users_groups")}`}
          value={getOptionsUsers.filter((opt) =>
            state.users?.groups?.includes(opt.value),
          )}
          onChange={(e: { label: string; value: number }[]) => {
            const selectedValues = e.map(
              (option: { label: string; value: number }) => option,
            );
            dispatch({
              type: "SET_USERS",
              payload: {
                ...state.users,
                groups: selectedValues.map(({ value }) => value),
              },
            });
          }}
          required={!allUsersSelected}
          options={getOptionsUsers}
          disabled={allUsersSelected}
          error={!!errors.usersGroups}
          errorMessage={`${t(`validation.${errors.usersGroups}`)}`}
        />
      </Box>
      <Box
        sx={{
          display: isSmallScreen ? "block" : "flex",
          justifyContent: "space-between",
          margin: "30px 10px 16px 10px",
          gap: 1,
        }}
      >
        <SingleCheckboxCst
          key={"flagExcludeUsers"}
          id={"flagExcludeUsers"}
          value={hasExcludedUsers}
          label={`${t("exclude_users")}`}
          onChange={(e) => {
            if (hasExcludedUsers) {
              dispatch({
                type: "SET_USERS",
                payload: { ...state.users, exclusions: undefined },
              });
            }
            setHasExcludedUsers(!hasExcludedUsers);
          }}
        />
        <DownloaderUploaderCst
          name={`${t("upload")}`}
          accept={"text/csv"}
          type={["upload", "download", "template"]}
          disabled={!hasExcludedUsers}
          downloadDisabled={!idCampaign || !hasExcludedUsers}
          fileName={idCampaign && hasExcludedUsers ? "excluded_users.csv" : ""}
          onChange={(e) =>
            dispatch({
              type: "SET_USERS",
              payload: { ...state.users, exclusions: e ?? undefined },
            })
          }
          onDownload={() => {
            if (idCampaign && hasExcludedUsers) {
              downloadFile(
                ServicesURL.findCampaignExclusions
                  .replace(":corporateId", id ? id : "1")
                  .replace(":campaignId", idCampaign ? idCampaign : ""),
                false,
                "excluded_users.csv",
                { Accept: "text/csv" },
              );
            }
          }}
          onTemplateDownload={() =>
            downloadFile(
              `${ServicesURL.getUsersExcludedTemplate}`,
              false,
              "template_excluded_users.csv",
            )
          }
          onDelete={() => {
            dispatch({
              type: "SET_USERS",
              payload: { ...state.users, exclusions: undefined },
            });
          }}
          maxSize={1000}
        />
      </Box>
    </Box>
  );
};

export default Step4;
